import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import RichTextEditor from './RichTextEditor';

const AnnouncementEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    category: 'general',
    priority: 'medium',
    icon: 'fas fa-bullhorn',
    color: 'crimson',
    attachments: [],
    targetAudience: [],
    expiryDate: '',
    isActive: true,
    isPinned: false,
    publishedAt: new Date().toISOString().split('T')[0]
  });

  const categories = [
    { id: 'admission', name: 'Admission', icon: 'fas fa-bullhorn', color: 'crimson' },
    { id: 'examination', name: 'Examination', icon: 'fas fa-file-alt', color: 'blue-500' },
    { id: 'event', name: 'Events', icon: 'fas fa-calendar', color: 'green-500' },
    { id: 'scholarship', name: 'Scholarship', icon: 'fas fa-award', color: 'orange-500' },
    { id: 'placement', name: 'Placement', icon: 'fas fa-building', color: 'purple-500' },
    { id: 'fee', name: 'Fee Notice', icon: 'fas fa-exclamation-triangle', color: 'red-500' },
    { id: 'result', name: 'Results', icon: 'fas fa-graduation-cap', color: 'indigo-500' },
    { id: 'workshop', name: 'Workshops', icon: 'fas fa-robot', color: 'teal-500' },
    { id: 'holiday', name: 'Holidays', icon: 'fas fa-umbrella-beach', color: 'pink-500' },
    { id: 'sports', name: 'Sports', icon: 'fas fa-trophy', color: 'yellow-600' },
    { id: 'general', name: 'General', icon: 'fas fa-info-circle', color: 'gray-500' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'green' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'high', label: 'High', color: 'orange' },
    { value: 'urgent', label: 'Urgent', color: 'red' }
  ];

  const targetAudienceOptions = [
    'All Students',
    'First Year',
    'Second Year', 
    'Third Year',
    'Final Year',
    'Faculty',
    'Staff',
    'Parents'
  ];

  useEffect(() => {
    if (isEdit && id) {
      fetchAnnouncement();
    }
  }, [id, isEdit]);

  const fetchAnnouncement = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAnnouncement(id);
      const announcement = response.data.announcement;
      
      setFormData({
        ...announcement,
        publishedAt: announcement.publishedAt ? announcement.publishedAt.split('T')[0] : '',
        expiryDate: announcement.expiryDate ? announcement.expiryDate.split('T')[0] : '',
        attachments: announcement.attachments || [],
        targetAudience: announcement.targetAudience || []
      });
    } catch (error) {
      console.error('Error fetching announcement:', error);
      setError('Failed to fetch announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-update icon and color when category changes
    if (field === 'category') {
      const categoryData = categories.find(cat => cat.id === value);
      if (categoryData) {
        setFormData(prev => ({
          ...prev,
          icon: categoryData.icon,
          color: categoryData.color
        }));
      }
    }
  };

  const handleTargetAudienceChange = (audience) => {
    const updatedAudience = formData.targetAudience.includes(audience)
      ? formData.targetAudience.filter(a => a !== audience)
      : [...formData.targetAudience, audience];
    
    setFormData(prev => ({ ...prev, targetAudience: updatedAudience }));
  };

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/webp'
    ];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setError(`File "${file.name}" is not allowed. Accepted: PDF, DOC, DOCX, JPG, PNG, WEBP`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`File "${file.name}" exceeds 10MB limit`);
        return;
      }
    }

    setUploading(true);
    setError('');

    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiService.uploadFile(file, 'attachments');
        if (response.data && response.data.file) {
          uploaded.push({
            filename: response.data.file.filename,
            originalName: response.data.file.originalName,
            url: response.data.file.url,
            size: response.data.file.size,
            mimeType: response.data.file.mimeType
          });
        }
      }

      setFormData(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...uploaded]
      }));
    } catch (err) {
      console.error('Attachment upload error:', err);
      setError('Failed to upload attachment. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return 'fas fa-file';
    if (mimeType.includes('pdf')) return 'fas fa-file-pdf text-red-500';
    if (mimeType.includes('word') || mimeType.includes('doc')) return 'fas fa-file-word text-blue-500';
    if (mimeType.includes('image')) return 'fas fa-file-image text-green-500';
    return 'fas fa-file text-gray-500';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Client-side validation
      if (!formData.title || !formData.description || !formData.content || !formData.category) {
        setError('Please fill in all required fields (Title, Description, Content, Category)');
        setSaving(false);
        return;
      }

      // Prepare data for submission
      const submitData = {
        ...formData,
        publishedAt: formData.publishedAt ? new Date(formData.publishedAt).toISOString() : new Date().toISOString(),
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null,
        // Ensure arrays are properly formatted
        attachments: formData.attachments || [],
        targetAudience: formData.targetAudience || []
      };

      console.log('Submitting announcement data:', submitData);
      console.log('API base URL:', apiService.baseURL);
      console.log('isEdit:', isEdit, 'id:', id);

      if (isEdit && id && id !== 'new') {
        const response = await apiService.updateAnnouncement(id, submitData);
        console.log('Update response:', response);
        setSuccess('Announcement updated successfully!');
      } else {
        const response = await apiService.createAnnouncement(submitData);
        console.log('Create response:', response);
        setSuccess('Announcement created successfully!');
        setTimeout(() => {
          navigate('/admin/announcements');
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      
      // Handle different error types
      let errorMessage = 'Failed to save announcement';
      
      if (error.message.includes('timeout')) {
        errorMessage = 'Request timeout - please check if the backend server is running';
      } else if (error.message.includes('401')) {
        errorMessage = 'Authentication failed - please log in again';
        navigate('/admin/login');
        return;
      } else if (error.message.includes('403')) {
        errorMessage = 'You do not have permission to create announcements';
      } else if (error.message.includes('400')) {
        errorMessage = 'Invalid data provided - please check all fields';
      } else if (error.message.includes('fetch')) {
        errorMessage = 'Network error - please check if the backend server is running';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading announcement...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-sans font-bold text-slate">
          {isEdit ? 'Edit Announcement' : 'Create New Announcement'}
        </h1>
        <button
          onClick={() => navigate('/admin/announcements')}
          className="px-4 py-2 bg-gray-100 text-slate rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2"
        >
          <i className="fas fa-arrow-left"></i>
          Back to Announcements
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Enter announcement title"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={3}
                placeholder="Brief description of the announcement"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                required
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>{priority.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Content</h2>
          
          <div>
            <label className="block text-sm font-medium text-slate mb-2">Full Content *</label>
            <RichTextEditor
              value={formData.content}
              onChange={(value) => handleInputChange('content', value)}
              placeholder="Enter the detailed content of the announcement..."
            />
          </div>
        </div>

        {/* Attachments */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Attachments</h2>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleAttachmentUpload}
                className="hidden"
                id="attachment-upload"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                multiple
              />
              <label
                htmlFor="attachment-upload"
                className={`px-4 py-2 bg-crimson text-white rounded-lg font-medium cursor-pointer hover:bg-crimson/90 flex items-center gap-2 ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paperclip"></i>
                    Add Attachment
                  </>
                )}
              </label>
              <span className="text-sm text-gray-500">PDF, DOC, DOCX, JPG, PNG, WEBP (Max 10MB each)</span>
            </div>

            {formData.attachments && formData.attachments.length > 0 && (
              <div className="space-y-2">
                {formData.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <i className={getFileIcon(attachment.mimeType)}></i>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate truncate">
                        {attachment.originalName || attachment.filename}
                      </p>
                      <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                    </div>
                    {attachment.url && (
                      <a
                        href={attachment.url.startsWith('http') ? attachment.url : `${apiService.baseURL.replace(/\/api$/, '')}${attachment.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm px-2"
                        title="View file"
                      >
                        <i className="fas fa-external-link-alt"></i>
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1"
                      title="Remove attachment"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(!formData.attachments || formData.attachments.length === 0) && (
              <p className="text-sm text-gray-400 italic">No attachments added yet</p>
            )}
          </div>
        </div>

        {/* Display Settings */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Display Settings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Icon</label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="e.g., fas fa-bullhorn"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="e.g., crimson, blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Published Date</label>
              <input
                type="date"
                value={formData.publishedAt}
                onChange={(e) => handleInputChange('publishedAt', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Expiry Date (Optional)</label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>
          </div>
        </div>

        {/* Target Audience */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Target Audience</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {targetAudienceOptions.map(audience => (
              <label key={audience} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.targetAudience.includes(audience)}
                  onChange={() => handleTargetAudienceChange(audience)}
                  className="rounded border-border_gray text-crimson focus:ring-crimson"
                />
                <span className="text-sm text-slate">{audience}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Status Settings */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Status & Visibility</h2>
          
          <div className="space-y-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="rounded border-border_gray text-crimson focus:ring-crimson"
              />
              <div>
                <span className="text-sm font-medium text-slate">Active</span>
                <p className="text-xs text-gray-600">Make this announcement visible to users</p>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPinned}
                onChange={(e) => handleInputChange('isPinned', e.target.checked)}
                className="rounded border-border_gray text-crimson focus:ring-crimson"
              />
              <div>
                <span className="text-sm font-medium text-slate">Pinned</span>
                <p className="text-xs text-gray-600">Pin this announcement to show at the top</p>
              </div>
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/announcements')}
            className="px-6 py-3 bg-gray-100 text-slate rounded-lg font-medium hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                {isEdit ? 'Update Announcement' : 'Create Announcement'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AnnouncementEdit;